import React, { useEffect, useRef, useState, useCallback } from 'react';
import { setAssistantSelectionFromElements } from '../../utils/assistantContext';

/**
 * SelectionOverlay — a full-viewport layer for point-and-click context.
 * Activated by 'banyak:assistant_select_mode'. Two gestures:
 *   • CLICK an element   → selects the element under the cursor
 *   • CLICK-DRAG a box   → selects every tagged element intersecting the box
 *
 * Live dashed highlighting follows the cursor / drag box. On commit we hand
 * the chosen DOM element(s) to the resolver, which turns them into a
 * structured (or text-fallback) selection context. Esc or right-click cancels.
 *
 * The overlay captures pointer events so clicks don't fall through to the UI.
 * It ignores the assistant sidebar itself (so you can't select the panel).
 */

const DRAG_THRESHOLD = 6; // px before a click becomes a drag

export default function SelectionOverlay() {
  const [active, setActive] = useState(false);
  const [hoverRect, setHoverRect] = useState(null);   // element hover highlight
  const [dragRect, setDragRect] = useState(null);     // rubber-band rectangle
  const dragStart = useRef(null);
  const isDragging = useRef(false);
  const overlayRef = useRef(null);

  // Enter/exit select mode on event.
  useEffect(() => {
    const on = () => setActive(true);
    const off = () => setActive(false);
    const toggle = () => setActive(a => !a);
    window.addEventListener('banyak:assistant_select_mode', on);
    window.addEventListener('banyak:assistant_select_mode_off', off);
    window.addEventListener('banyak:assistant_select_mode_toggle', toggle);
    return () => {
      window.removeEventListener('banyak:assistant_select_mode', on);
      window.removeEventListener('banyak:assistant_select_mode_off', off);
      window.removeEventListener('banyak:assistant_select_mode_toggle', toggle);
    };
  }, []);

  const exit = useCallback(() => {
    setActive(false);
    setHoverRect(null);
    setDragRect(null);
    dragStart.current = null;
    isDragging.current = false;
    window.dispatchEvent(new CustomEvent('banyak:assistant_select_mode_ended'));
  }, []);

  // Escape cancels.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === 'Escape') exit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, exit]);

  // Is a point inside the assistant sidebar (which we never select)?
  const inSidebar = (x, y) => {
    const el = document.elementFromPoint(x, y);
    return !!el?.closest?.('#assistant-chat-slot, [data-assistant-sidebar]');
  };

  // Find the most meaningful element under the cursor: prefer a tagged
  // ancestor (data-context-id), else the element itself.
  const pickElement = (x, y) => {
    // Temporarily let elementFromPoint see through the overlay.
    const overlay = overlayRef.current;
    const prevPE = overlay ? overlay.style.pointerEvents : null;
    if (overlay) overlay.style.pointerEvents = 'none';
    const el = document.elementFromPoint(x, y);
    if (overlay) overlay.style.pointerEvents = prevPE;
    if (!el) return null;
    return el.closest('[data-context-id]') || el;
  };

  const onMove = (e) => {
    if (!active) return;
    const { clientX: x, clientY: y } = e;

    if (dragStart.current) {
      const dx = x - dragStart.current.x;
      const dy = y - dragStart.current.y;
      if (!isDragging.current && Math.hypot(dx, dy) > DRAG_THRESHOLD) isDragging.current = true;
      if (isDragging.current) {
        setHoverRect(null);
        setDragRect({
          left: Math.min(x, dragStart.current.x),
          top: Math.min(y, dragStart.current.y),
          width: Math.abs(dx),
          height: Math.abs(dy),
        });
      }
      return;
    }

    // Hover highlight (not dragging).
    if (inSidebar(x, y)) { setHoverRect(null); return; }
    const el = pickElement(x, y);
    if (el) {
      const r = el.getBoundingClientRect();
      setHoverRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    } else {
      setHoverRect(null);
    }
  };

  const onDown = (e) => {
    if (!active || e.button !== 0) { if (e.button === 2) exit(); return; }
    dragStart.current = { x: e.clientX, y: e.clientY };
    isDragging.current = false;
  };

  const onUp = (e) => {
    if (!active) return;
    const { clientX: x, clientY: y } = e;

    if (isDragging.current && dragRect) {
      // Rectangle: gather tagged elements intersecting the box.
      const box = dragRect;
      const tagged = Array.from(document.querySelectorAll('[data-context-id]')).filter(el => {
        const r = el.getBoundingClientRect();
        const intersects = !(r.right < box.left || r.left > box.left + box.width || r.bottom < box.top || r.top > box.top + box.height);
        return intersects && !el.closest('#assistant-chat-slot, [data-assistant-sidebar]');
      });
      if (tagged.length) {
        setAssistantSelectionFromElements(tagged);
      } else {
        // No tagged elements in the box → fall back to whatever element is
        // under the box center (text extraction).
        const el = pickElement(box.left + box.width / 2, box.top + box.height / 2);
        if (el) setAssistantSelectionFromElements([el]);
      }
    } else {
      // Click: single element under cursor.
      if (!inSidebar(x, y)) {
        const el = pickElement(x, y);
        if (el) setAssistantSelectionFromElements([el]);
      }
    }
    exit();
  };

  if (!active) return null;

  return (
    <div
      ref={overlayRef}
      onMouseMove={onMove}
      onMouseDown={onDown}
      onMouseUp={onUp}
      onContextMenu={(e) => { e.preventDefault(); exit(); }}
      className="fixed inset-0 z-[9999] cursor-crosshair"
      style={{ background: 'rgba(0,0,0,0.02)' }}
    >
      {/* Hint banner */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-gray-900/90 text-white text-xs font-medium shadow-lg pointer-events-none">
        Click an item or drag a box to give the assistant context · Esc to cancel
      </div>

      {/* Hover element highlight */}
      {hoverRect && (
        <div
          className="absolute border-2 border-dashed border-emerald-500 bg-emerald-400/10 rounded pointer-events-none"
          style={{ left: hoverRect.left, top: hoverRect.top, width: hoverRect.width, height: hoverRect.height }}
        />
      )}

      {/* Drag rectangle */}
      {dragRect && (
        <div
          className="absolute border-2 border-dashed border-emerald-500 bg-emerald-400/10 rounded pointer-events-none"
          style={{ left: dragRect.left, top: dragRect.top, width: dragRect.width, height: dragRect.height }}
        />
      )}
    </div>
  );
}
